import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import { FormInlined } from "../../business-objects/form/form.model";
import { GroupInlined } from "../../business-objects/group/group.model";

interface IFormViewState {
    form: FormInlined;
    activeGroupIndex: number;
    targetGroupIndex: number;
    activeSlideIndex: number;
    slideCount: number;
    formProgress: number;
}

interface IFormViewStateUpdate {
    form?: FormInlined;
    activeGroupIndex?: number;
    targetGroupIndex?: number;
    activeSlideIndex?: number;
    slideCount?: number;
    formProgress?: number;
}

@Injectable()
export class FormStateService {

    protected state: IFormViewState = {
        form: undefined,
        activeGroupIndex: 0,
        targetGroupIndex: 0,
        activeSlideIndex: 0,
        slideCount: 0,
        formProgress: 0
    };

    protected state$: BehaviorSubject<IFormViewState> = new BehaviorSubject<IFormViewState>(this.state);

    protected getState(): Observable<IFormViewState> {
        return this.state$;
    }

    protected updateState(state: IFormViewStateUpdate): void {

        this.state = {
            ...this.state,
            ...state
        };

        this.state$.next(this.state);
    }

    get activeGroup$(): Observable<GroupInlined> {
        return this.state$
            .map(state => {

                if (!state.form) {
                    return undefined;
                }

                if (state.activeGroupIndex < 0 || state.activeGroupIndex > state.form.groups.length - 1) {
                    return undefined;
                }

                return state.form.groups[state.activeGroupIndex];
            });
    }

    get activeGroupIndex$(): Observable<number> {
        return this.state$
            .map(state => state.activeGroupIndex);
    }

    get displayingGroups$(): Observable<Array<GroupInlined>> {

        return this.state$
            .map(state => {

                if (!state.form) {
                    return [];
                }

                const targetL = Math.max(state.activeGroupIndex - 1, 0);
                const targetR = Math.min(state.activeGroupIndex + 2, state.slideCount);
                return state.form.groups.slice(targetL, targetR);
            });
    }

    get form$(): Observable<FormInlined> {
        return this.state$
            .map(state => state.form);
    }

    get progress$(): Observable<number> {
        return this.state$
            .map(state => (state.activeGroupIndex + 1) / state.slideCount);
    }

    get showNextButton$(): Observable<boolean> {
        return this.state$
            .map(state => state.activeGroupIndex !== state.slideCount - 1);
    }

    get showPrevButton$(): Observable<boolean> {
        return this.state$
            .map(state => state.activeGroupIndex !== 0);
    }

    get slideCount$(): Observable<number> {
        return this.state$
            .map(state => state.slideCount);
    }

    get subTitle$(): Observable<string> {
        return this.state$
            .map(state => {

                if (!state.form) {
                    return "";
                }

                const group = state.form.groups[state.activeGroupIndex];
                if (!group) {
                    return "";
                }
                return group.title;
            });
    }

    get targetGroupIndex$(): Observable<number> {
        return this.state$
            .map(state => state.targetGroupIndex);
    }

    get title$(): Observable<string> {
        return this.state$
            .map(state => {
                if (!state.form) {
                    return "";
                }
                return state.form.title;
            });
    }

    get slideFromIndex$(): Observable<number> {
        return this.state$
            .map(state => {
                switch (true) {
                    case state.activeGroupIndex === 0: {
                        return 0;
                    }
                    case state.activeGroupIndex < state.targetGroupIndex: {
                        return 1;
                    }
                    case state.activeGroupIndex > state.targetGroupIndex: {
                        return 1;
                    }
                    case state.activeGroupIndex === state.slideCount - 1: {
                        return 1;
                    }
                    default: {
                        return 1;
                    }
                }
            });
    }

    get slideToIndex$(): Observable<number> {
        return this.state$
            .map(state => {
                switch (true) {
                    case state.activeGroupIndex === 0: {
                        return 1;
                    }
                    case state.activeGroupIndex < state.targetGroupIndex: {
                        return 2;
                    }
                    case state.activeGroupIndex > state.targetGroupIndex: {
                        return 0;
                    }
                    case state.activeGroupIndex === state.slideCount - 1: {
                        return 0;
                    }
                    default: {
                        return 1;
                    }
                }
            });
    }

    setActiveGroupIndex(i: number): void {
        this.updateState({
            activeGroupIndex: i
        });
    }

    setForm(form: FormInlined): void {

        if (!form) {
            return;
        }

        this.updateState({
            form,
            slideCount: form.groups.length
        });
    }

    setTargetGroupIndex(index: number): void {

        this.updateState({
            targetGroupIndex: index
        });

    }

    setTargetAndActiveGroupIndexes(activeGroupIndex: number, targetGroupIndex: number): void {
        this.updateState({
            activeGroupIndex,
            targetGroupIndex
        });
    }
}



// WEBPACK FOOTER //
// ./src/vas/pages/form/form-state.service.ts